import { Anchor, Button, Separator, styled, Text, View, XStack } from "tamagui";

import { Hamburger, Logo } from "../../icons";

const HeaderContainer = styled(View, {
  name: "HeaderContainer",
  justifyContent: "flex-end",
  alignItems: "center",
  alignSelf: "center",
  width: "100%",
  backgroundColor: "$white",
  variants: {
    media: {
      lg: {
        height: "$10.5",
      },
      md: {
        height: "$7.5",
      },
    },
  } as const,
  defaultVariants: {
    media: "lg",
  },
});

const HeaderContentContainer = styled(View, {
  name: "HeaderContentContainer",
  width: "100%",
  maxWidth: 1248,
  marginHorizontal: "$3",
  justifyContent: "space-between",
  alignItems: "flex-end",
  flexDirection: "row",
  variants: {
    media: {
      lg: {
        height: "$4.1",
      },
      md: {
        height: "$3",
        paddingHorizontal: "$3",
      },
    },
  } as const,
  defaultVariants: {
    media: "lg",
  },
});

const HeaderBottomLineContainer = styled(View, {
  name: "HeaderContentContainer",
  width: "100%",
  maxWidth: 1248,
  marginHorizontal: "$3",
  justifyContent: "space-between",
  alignItems: "flex-end",
  flexDirection: "row",

  variants: {
    media: {
      lg: {
        height: "$3.4",
      },
      md: {
        height: "$2",
        paddingHorizontal: 0,
      },
    },
  } as const,
  defaultVariants: {
    media: "lg",
  },
});

const HeaderText = styled(Text, {
  name: "HeaderText",
  variants: {
    font: {
      lg: {
        fontFamily: "$body",
        fontWeight: "$5",
        fontmedia: "$3",
        lineHeight: "$3",
      },
      md: {
        fontFamily: "$subtitle",
        fontWeight: "$4",
        fontSize: "$2",
        lineHeight: "$2",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

const HeaderBottomLine = styled(Separator, {
  name: "HeaderBottomLine",
  width: "100%",
  variants: {
    media: {
      lg: {
        borderColor: "$gray-primary-900",
        borderBottomWidth: "$0.2",
      },

      md: {
        borderColor: "$gray-primary-900",
        borderBottomWidth: "$0.1",
      },
    },
  } as const,
  defaultVariants: {
    media: "lg",
  },
});

const NavItemsText = styled(Text, {
  name: "NavItemsText",
  fontFamily: "$body",
  fontSize: "$3",
  lineHeight: "$3",
  variants: {
    active: {
      true: {
        fontWeight: "$6",
        color: "$gray-primary-900",
      },
      false: {
        fontWeight: "$5",
        color: "$gray-600",
      },
    },
  } as const,
  defaultVariants: {
    active: false,
  },
});

const NavItemsContainer = styled(View, {
  name: "NavItemsContainer",
  height: "$4",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  variants: {
    active: {
      true: {
        borderBottomWidth: "$0.2",
        borderBottomColor: "$pinkmain-500",
      },
      false: {},
    },
  } as const,
  defaultVariants: {
    active: false,
  },
});

type NavItemProps = React.ComponentProps<typeof NavItemsContainer>;

const NavItem: React.FC<
  NavItemProps & { children: string; active?: boolean }
> = ({ children, active = false, ...rest }) => {
  return (
    <NavItemsContainer active={active} {...rest}>
      <NavItemsText active={active}>{children}</NavItemsText>
    </NavItemsContainer>
  );
};

type HeaderProps = React.ComponentProps<typeof HeaderContainer>;

type ButtonProps = React.ComponentProps<typeof Button>;

const HamburgerButton: React.FC<ButtonProps> = () => {
  return (
    <Button
      backgroundColor={"transparent"}
      borderWidth={0}
      padding={0}
      margin={0}
      height="$3"
    >
      <Hamburger />
    </Button>
  );
};

export const Body: React.FC<
  {
    progressBar?: React.ReactNode;
    userName: string | undefined;
    renderButton?: React.ReactNode;
    children?: React.ReactNode;
    logo?: React.ReactNode;
  } & HeaderProps
> = ({
  progressBar,
  userName,
  renderButton,
  children,
  media = "lg",
  logo,
  ...rest
}) => {
  return (
    <HeaderContainer media={media} {...rest}>
      <HeaderContentContainer media={media}>
        {!logo ? (
          <Anchor href={"/"} unstyled={true}>
            <Logo size={media} />
          </Anchor>
        ) : (
          logo
        )}

        {children}
        <XStack gap="$1" alignItems="center" height={"100%"}>
          {userName && <HeaderText font={media}>{userName}</HeaderText>}
          {renderButton}
        </XStack>
      </HeaderContentContainer>
      <HeaderBottomLineContainer media={media}>
        {progressBar ? progressBar : <HeaderBottomLine media={media} />}
      </HeaderBottomLineContainer>
    </HeaderContainer>
  );
};

export const Header = {
  Container: HeaderContainer,
  Content: HeaderContentContainer,
  Body,
  HamburgerButton,
  NavItem,
  BottomLineContainer: HeaderBottomLineContainer,
  BottomLine: HeaderBottomLine,
};
